<template>
  <div class="file-upload-container" 
      @dragenter.stop="onDragEnter"
      @dragleave.stop="onDragLeave"
      @dragover.stop="onDragOver"
      @drop.stop="onDrop"
      @click="$refs.fileinput.click()">
      <div class="textnode">
        <div class="icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="35" height="25" viewBox="0 0 35 25" style="position:relative; top: 5px;"><g fill="none"><g fill="#FF1B55"><path d="M24.2 24.6L23 24.6C22.5 24.6 22.1 24.2 22.1 23.8 22.1 23.3 22.5 22.9 23 22.9L24.2 22.9C29 22.9 32.8 19.1 32.8 14.3 32.8 11.1 30.4 8.3 27.2 7.8 26.9 7.8 26.6 7.6 26.5 7.3 25.4 4.1 22.3 1.9 18.9 1.9 15.2 1.9 12 4.3 11.1 7.8 11 8.2 10.6 8.4 10.3 8.4L8.8 8.4C4.9 8.4 1.8 11.6 1.8 15.5 1.8 19.6 5.1 22.9 9.2 22.9L11.1 22.9C11.5 22.9 11.9 23.3 11.9 23.8 11.9 24.2 11.5 24.6 11.1 24.6L9.2 24.6C4.2 24.6 0.1 20.5 0.1 15.5 0.1 10.7 4 6.8 8.8 6.8L9.7 6.8C11 2.9 14.7 0.3 18.9 0.3 22.8 0.3 26.4 2.7 27.9 6.3 31.7 7.1 34.5 10.4 34.5 14.3 34.5 20 29.9 24.6 24.2 24.6L24.2 24.6Z"/><path d="M22.3 16.6L17.7 12C17.5 11.9 17.3 11.8 17.1 11.8 16.9 11.8 16.7 11.9 16.5 12L12 16.6C11.7 16.9 11.7 17.4 12 17.7 12.1 17.9 12.3 18 12.6 18 12.8 18 13 17.9 13.1 17.7L16.3 14.6 16.3 24.1C16.3 24.6 16.7 25 17.1 25 17.6 25 17.9 24.6 17.9 24.1L17.9 14.6 21.1 17.7C21.4 18.1 21.9 18.1 22.3 17.7 22.6 17.4 22.6 16.9 22.3 16.6L22.3 16.6Z"/></g></g></svg>
        </div>
        <br/>
        Drop JPEG files to upload.
      </div>
      <transition name="fade">
        <div class="upload-status">
          <span v-if="err != null" class="red pulse">{{ err }}</span>
          <template v-else>
            <span v-if="uploadingFiles">Uploading files <i class="fas fa-circle-notch fa-spin"></i></span>
            <span v-if="processingFiles">Processing files <i class="fas fa-cog fa-spin"></i></span>
            <span v-if="uploadComplete">Upload complete <i class="fa fa-check"></i></span>
          </template>
        </div>
      </transition>
      <transition name="fade">
        <div v-if="progress != 0" class="progress-bar-container">
          <progress-bar :progress="progress"/>
        </div>
      </transition>
      <input type="file" accept="image/jpeg" style="display:none" @change="manuallySelectedUpload" ref="fileinput" multiple/> 
      
      <div class="info">max. 10MB per JPEG</div>
  </div>
</template>

<script>
import fileUploadService from '@/services/file-upload-service.js'

import ProgressBar from '@/components/utility/ProgressBar.vue'

export default {
  name: 'FileUpload',
  components: {
    ProgressBar,
  },
  props: {
    projectName: {
      type: String,
      required: true,
    }
  },
  data() {
    return {
      progress: 0,
      uploadInProgress: false,

      err: null,
    }
  },
  methods: {
    onDragEnter(e) {
      e.preventDefault()
    },

    onDragLeave(e) {
      e.preventDefault();
    },

    onDragOver(e) {
      e.preventDefault();
    },
    onDrop(e) {
      e.preventDefault();
      
      this.err = null
      let files = e.dataTransfer.files;

      for(let i = 0; i < files.length; i++) {
        // console.log(files[i].type)
        if(files[i].type != 'image/jpeg') {
          this.err = 'Invalid file type. Please only upload JPEG files.'
          return
        }
      }
      this.upload(files)
    },
    manuallySelectedUpload(e) {
      this.err = null
      let files = e.target.files
      for(let i = 0; i < files.length; i++) {
        if(files[i].type != 'image/jpeg') {
          this.err = 'Invalid file type. Please only upload JPEG files.'
          return
        }
      }
      this.upload(files)
    },
    async upload(files) {
      files = Array.from(files) // turn FileList into Array
      this.err = null

      if(!this.projectName) {
        this.err = 'invalid configuration, cannot upload.'
        return
      }

      this.uploadInProgress = true
      this.progress = 0

      var promises = []

      const progressCallback = (progressEvent) => this.progress += Math.round((progressEvent.loaded*100) / progressEvent.total / files.length)

      files.forEach(f => {
        // console.log(f)
        promises.push(fileUploadService.galleryUpload(this.projectName, f, progressCallback))
      })

      await Promise.all(promises)
        .catch(err => this.err = err)

      this.uploadInProgress = false
      this.$emit('upload-complete')
    },
  },
  computed: {
    uploadingFiles() {
      return this.uploadInProgress && this.progress < 100
    },
    uploadComplete() {
      return !this.uploadInProgress && this.progress >= 100
    },
    processingFiles() {
      return this.progress >= 100 && this.uploadInProgress
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/shared.scss';

.file-upload-container {
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 6;
  box-sizing: border-box;
  border: 2px dashed rgba($primary, 0.5);
  text-align: center;
  cursor: pointer;

  &:hover {
    border-color: rgba($primary, 1);

    .info {
      opacity: 1;
    }
  }

  .textnode {
    z-index: 5;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    transition: 0.3s all;

    text-align: center;

    .icon {
      width: 60px;
      height: 60px;
      line-height: 60px;
      border-radius: 50%;
      background-color: $background;
      margin: auto;
    }
  }

  .upload-status {
    position: absolute;
    bottom: 20px;
    padding: 0px 20px;
    width: calc(100% - 20px);
    text-align: left;
  }

  .progress-bar-container {
    position: absolute;
    bottom: 10px;
    padding: 0px 10px;
    width: calc(100% - 20px);
  }

  .info {
    text-align: right;
    position: absolute;
    right: 5px;
    top: 5px;
    color: $primaryText;
    font-size: 16px;
    opacity: 0;
    transition: opacity 0.2s;
  }
}
</style>